/**
 * Mounts the native push bridge, asks for permission once, and shows an
 * incoming push as a dialog. CM-412.
 *
 * Render once, inside the StoreProvider, from a component that renders on every
 * route. Renders nothing at all outside the native shell.
 */

import { BaseDialog, Typography } from "@coolremote/shared";
import { useStoreState } from "easy-peasy";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { pushRegistration as pushNotificationService } from "coolremote-sdk";
import styles from "./PushNotificationsGate.module.css";

/** Long enough for the app's own loaders to settle so the sheet is not lost
 *  behind a spinner. Deliberately not on cold start. */
const PROMPT_DELAY_MS = 1500;

/** Fired on window for every push shown, so open screens (the Alert Log)
 *  can reload instead of missing the alert the popup announces (CM-893). */
export const PUSH_RECEIVED_EVENT = "cr-push-received";

/** How many alerts the grouped popup lists before summarising the rest. */
const MAX_LISTED_ALERTS = 5;

/** Same alert twice (e.g. a retried send) must not count or list twice. */
const pushKey = (p: any) => `${(p && p.title) || ''}|${(p && p.message) || ''}`;
const appendUnique = (current: any[], incoming: any[]) => {
  const seen = new Set(current.map(pushKey));
  const added = incoming.filter((p) => {
    const key = pushKey(p);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return added.length ? [...current, ...added] : current;
};

const PushNotificationsGate: React.FC = () => {
  const me = useStoreState((s: any) => s.users.me);
  const isLoggedIn = useStoreState((s: any) => s.isLoggedIn);
  const isInitialized = useStoreState((s: any) => s.isInitialized);
  const [isSupported, setIsSupported] = useState(false);
  // Every push received since the popup was last dismissed. More than one is
  // rendered as a single grouped popup that updates as alerts arrive (CM-819).
  const [pushMessages, setPushMessages] = useState<any[]>([]);
  const history = useHistory();

  // Listeners are attached once, so route through a ref rather than closing
  // over a history instance that a remount would stale.
  const historyRef = useRef(history);
  historyRef.current = history;

  // Listeners need the current user without re-subscribing on every change.
  const meRef = useRef(me);
  meRef.current = me;
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  const isInitializedRef = useRef(isInitialized);
  isInitializedRef.current = isInitialized;

  // A cold-start tap arrives before initApp has loaded displayFlags, and the
  // mobile /alerts route only exists once showAlertLog is known - navigating
  // then falls through the <Switch> to its <Redirect to="/" /> and the user
  // lands on the dashboard instead of the Alert Log (CM-818). Hold the route
  // until the store reports initialized.
  const pendingRouteRef = useRef<string | null>(null);

  // useHistory() is undefined when this is rendered outside the <Router>
  // (which lives inside Routes.tsx) - that silently broke tap navigation
  // once, so both the navigation and the badge tracking check for it.
  const navigateToAlerts = () => {
    try {
      historyRef.current?.push("/alerts");
    } catch (err) {
      /* routing is best-effort; the dialog still shows the message */
    }
  };

  // A tap means the user deliberately came from a notification, so land them
  // on the alert log; a foreground push must not move them off their screen.
  //
  // Nothing is shown without a session: the tray can still hold notifications
  // from before a logout, and a push can land before an expired session has
  // been cleaned up, so a popup here would show alert content on the login
  // screen to whoever is holding the phone (CM-816).
  const handlePushMessage = (payload: any) => {
    if (!isLoggedInRef.current) {
      return;
    }
    setPushMessages((current) => appendUnique(current, [payload]));
    window.dispatchEvent(new Event(PUSH_RECEIVED_EVENT));
    if (payload && payload.tapped) {
      if (isInitializedRef.current) {
        navigateToAlerts();
      } else {
        pendingRouteRef.current = "/alerts";
      }
    }
  };

  useEffect(() => {
    if (isInitialized && isLoggedIn && pendingRouteRef.current) {
      pendingRouteRef.current = null;
      navigateToAlerts();
    }
  }, [isInitialized, isLoggedIn]);

  // Badge (CM-818): the server owns the number. Pull it onto the icon once
  // the session is live and whenever the app returns to the foreground; clear
  // it - server and icon - whenever the Alert Log is open. The server call
  // also runs in a plain browser, so opening the Alert Log on a laptop clears
  // the phone's badge; only the icon update needs the shell.
  useEffect(() => {
    const router = historyRef.current;
    if (!isInitialized || !isLoggedIn || !router) {
      return undefined;
    }
    const isOnAlertLog = (pathname: string) =>
      pathname === "/alerts" || pathname.startsWith("/alerts/");
    const refresh = () => {
      if (isOnAlertLog(router.location.pathname)) {
        pushNotificationService.clearBadge();
      } else {
        pushNotificationService.syncBadge();
      }
    };
    refresh();
    const unlisten = router.listen((location: any) => {
      if (isOnAlertLog(location.pathname)) {
        pushNotificationService.clearBadge();
      }
    });
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      unlisten();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isInitialized, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setPushMessages([]);
      pendingRouteRef.current = null;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    let detach: (() => void) | undefined;
    let cancelled = false;

    // Cleans up the registration if the session is force-ended.
    pushNotificationService.installForcedLogoutHandler();

    pushNotificationService.initNativeBridge().then((supported: boolean) => {
      if (cancelled) {
        return;
      }
      setIsSupported(supported);
      if (supported) {
        detach = pushNotificationService.attachNativeListeners(
          () => meRef.current,
          handlePushMessage
        );
      }
    });

    return () => {
      cancelled = true;
      if (detach) {
        detach();
      }
    };
  }, []);

  useEffect(() => {
    // Gate on `me` being loaded, not just isLoggedIn: in this app isLoggedIn is
    // an optimistic default derived from a persisted token, so it can be true
    // before (or without) a live session.
    if (!isSupported || !isLoggedIn || !me) {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      // shouldOfferPrompt still gates this: it only resolves true while the OS
      // is at notDetermined and we have not asked before, so the prompt fires
      // at most once per install.
      pushNotificationService.shouldOfferPrompt().then((offer: boolean) => {
        if (!cancelled && offer) {
          pushNotificationService.requestPermissionAndRegister(meRef.current);
        }
      });
    }, PROMPT_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isSupported, isLoggedIn, me]);

  if (!isSupported) {
    return null;
  }

  const dismiss = () => setPushMessages([]);
  const count = pushMessages.length;
  const latest = count ? pushMessages[count - 1] : null;
  const grouped = count > 1;
  // Newest first, capped so a storm stays readable.
  const listed = grouped ? pushMessages.slice(-MAX_LISTED_ALERTS).reverse() : [];
  const unlisted = count - listed.length;

  return (
    <BaseDialog
      open={count > 0}
      onClose={dismiss}
      onConfirm={dismiss}
      title={grouped ? t`${count} new alerts` : latest ? latest.title : undefined}
      confirmLabel={t`OK`}
      hideCancel
      hideCloseButton
      className={styles.root}
    >
      {grouped ? (
        <>
          {listed.map((item: any, index: number) => (
            <div key={index} style={{ marginBottom: 12 }}>
              {item.title ? <Typography variant="optionTitle">{item.title}</Typography> : null}
              <Typography>{item.message}</Typography>
            </div>
          ))}
          {unlisted > 0 ? (
            <Typography color="muted">{t`and ${unlisted} more`}</Typography>
          ) : null}
        </>
      ) : (
        <Typography>{latest ? latest.message : ""}</Typography>
      )}
    </BaseDialog>
  );
};

export default PushNotificationsGate;
